import { Flex, useBreakpointValue } from '@chakra-ui/react'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { EmptyState } from '~/components/EmptyState'
import { CallsTable } from '~/pages/Calls/CallsTable'
import {
  CallTableColumn,
  CallTableColumnSorting,
} from '~/pages/Calls/data/CallTableData'
import {
  DateRange,
  SearchValue,
  SelectedFilterData,
  SortDirection,
} from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

import { Call } from '@mangovoice/types'

import { TCallAnalyticsTab } from './CallDetails/CallDetailsAnalyticsContent'
import { CallsListViewHeader } from './CallsListViewHeader'

interface CallsListViewProps {
  calls?: Call[]
  totalCallCount?: number
  searchValue?: SearchValue
  columnSorting?: CallTableColumnSorting[]
  dateTimeRange?: DateRange
  isLoading?: boolean
  onCallSelected?: (callId: number, initialTab?: TCallAnalyticsTab) => void
  onActiveFiltersChanged?: (activeFilters: SelectedFilterData[]) => void
  onDateTimeRangeChanged?: (dateTimeRange: DateRange) => void
  onEndReached?: () => void
  onSearchValueChanged?: (searchValue: SearchValue) => void
  onSortableColumnClicked?: (
    columnType: CallTableColumn,
    currentSortDirection?: SortDirection,
  ) => void
}

export const CallsListView = ({
  calls = [],
  searchValue,
  columnSorting,
  dateTimeRange,
  isLoading,
  onCallSelected,
  onActiveFiltersChanged,
  onDateTimeRangeChanged,
  onEndReached,
  onSearchValueChanged,
  onSortableColumnClicked,
}: CallsListViewProps) => {
  const { t } = useTranslation()
  const { callId } = useParams()
  const screenSizeRef = useRef('md')
  const screenSize =
    useBreakpointValue(
      {
        base: 'xs',
        sm: 'sm',
        md: 'md',
        lg: 'lg',
        xl: 'xl',
        '2xl': 'xxl',
      },
      { ssr: false },
    ) ?? screenSizeRef.current
  screenSizeRef.current = screenSize

  let tableSize: Size = Size.lg
  if (screenSize === 'xs' || (screenSize === 'md' && callId)) {
    tableSize = Size.sm
  } else if (screenSize === 'sm') {
    tableSize = Size.md
  } else if (screenSize === 'lg' && callId) {
    tableSize = Size.md
  }

  return (
    <Flex
      direction="column"
      flex="1 0 0"
      alignSelf="stretch"
      alignItems="flex-start"
    >
      <CallsListViewHeader
        dateTimeRange={dateTimeRange}
        searchValue={searchValue}
        tableSize={tableSize}
        onActiveFiltersChanged={onActiveFiltersChanged}
        onDateTimeRangeChanged={onDateTimeRangeChanged}
        onSearchValueChanged={onSearchValueChanged}
      />
      {calls.length === 0 && !isLoading ? (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100%"
          width="100%"
        >
          <EmptyState
            title={t('callsList.noCallsFound')}
            subtitle={t('callsList.tryDifferentFilters')}
          />
        </Flex>
      ) : (
        <CallsTable
          calls={calls}
          selectedCallId={Number(callId)}
          isLoading={isLoading}
          tableSize={tableSize}
          columnSorting={columnSorting}
          onCallSelected={onCallSelected}
          onEndReached={onEndReached}
          onSortableColumnClicked={onSortableColumnClicked}
        />
      )}
    </Flex>
  )
}
